import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { UserRole } from 'types/userRoles';
import { Select, Modal, Input, Button } from 'common/components';
import { emailRegisterOptions } from 'common/constants';
import { useCreateUser } from 'services/hooks/mutations';
import { AddUserModalProps, NewUserForm, PasswordValidation } from './AddUserModal.types';
import styles from './AddUserModal.module.scss';

const AddUserModal: React.FC<AddUserModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    onError,
    hasTenantAccess,
    tenants,
    selectedTenant,
    selectedUser,
    currentUserTenantId,
}) => {
    const {
        register,
        handleSubmit: handleFormSubmit,
        control,
        watch,
        reset,
        formState: { errors },
    } = useForm<NewUserForm>({
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            roles: [],
            tenantId: selectedTenant?.id || '',
        },
    });

    const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>({
        minLength: false,
        hasUpperCase: false,
        hasLowerCase: false,
        hasDigit: false,
        hasSpecialChar: false,
    });

    const { createUser, isPending } = useCreateUser();

    // Watch password field for validation
    const password = watch('password');

    // Validate password in real-time
    useEffect(() => {
        if (password) {
            setPasswordValidation({
                minLength: password.length >= 10,
                hasUpperCase: /[A-Z]/.test(password),
                hasLowerCase: /[a-z]/.test(password),
                hasDigit: /[0-9]/.test(password),
                hasSpecialChar: /[!@#$%^&*()_+\-=[\]{}();':"\\|,.<>/?]/.test(password),
            });
        } else {
            setPasswordValidation({
                minLength: false,
                hasUpperCase: false,
                hasLowerCase: false,
                hasDigit: false,
                hasSpecialChar: false,
            });
        }
    }, [password]);

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            reset();
            setPasswordValidation({
                minLength: false,
                hasUpperCase: false,
                hasLowerCase: false,
                hasDigit: false,
                hasSpecialChar: false,
            });
        }
    }, [isOpen, reset]);

    const onSubmit = async (data: NewUserForm) => {
        // Validate password requirements
        if (
            !passwordValidation.minLength ||
            !passwordValidation.hasUpperCase ||
            !passwordValidation.hasLowerCase ||
            !passwordValidation.hasDigit ||
            !passwordValidation.hasSpecialChar
        ) {
            onError('Password does not meet the requirements');
            return;
        }

        // Validate tenant selection if we have multiple tenants
        if (hasTenantAccess && !selectedTenant && !data.tenantId) {
            onError('Please select a tenant');
            return;
        }

        // Prepare user data object for API (using snake_case for API)
        const userData: any = {
            first_name: data.firstName,
            last_name: data.lastName,
            email: data.email.trim().toLowerCase(),
            password: data.password,
            roles: data.roles,
        };

        if (data.tenantId) {
            userData.tenant_id = data.tenantId;
        } else if (selectedTenant?.id) {
            userData.tenant_id = selectedTenant.id;
        } else if (!hasTenantAccess && currentUserTenantId) {
            userData.tenant_id = currentUserTenantId;
        }

        try {
            // Call API to create the user
            const response = await createUser(userData);

            // Get the created user from the response and map it to camelCase
            const createdUser = {
                id: response.id,
                firstName: response.first_name,
                lastName: response.last_name,
                email: response.email,
                tenantId: response.tenant_id,
                roles: response.roles,
                createdAt: response.created_at,
                updatedAt: response.updated_at,
                cognitoId: response.cognito_id,
                edges: response.edges,
            };

            // Check if user was added to a different tenant than currently selected
            const wasAddedToDifferentTenant = Boolean(
                data.tenantId && (!selectedTenant || data.tenantId !== selectedTenant.id)
            );

            // Close modal
            onClose();

            // Call success callback
            await onSuccess(createdUser, wasAddedToDifferentTenant);
        } catch (error: any) {
            console.error('Error creating user:', error);
            onError(error.response?.data?.message || 'Failed to create user. Please try again.');
        }
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={hasTenantAccess && selectedTenant ? `Add User to ${selectedTenant.name}` : 'Add User'}
            size='md'
            showDefaultFooter={false}
        >
            <form onSubmit={handleFormSubmit(onSubmit)} id='add-user-form' className='flex flex-col gap-md'>
                <Input
                    label='First Name'
                    type='text'
                    error={!!errors.firstName}
                    errorText={errors.firstName?.message}
                    {...register('firstName', { required: 'First name is required' })}
                />
                <Input
                    label='Last Name'
                    type='text'
                    error={!!errors.lastName}
                    errorText={errors.lastName?.message}
                    {...register('lastName', { required: 'Last name is required' })}
                />
                <Input
                    label='Email'
                    type='email'
                    error={!!errors.email}
                    errorText={errors.email?.message}
                    {...register('email', emailRegisterOptions)}
                />
                <div className='flex flex-col gap-sm'>
                    <Input
                        label='Password'
                        type='password'
                        showPasswordToggle
                        error={!!errors.password}
                        errorText={errors.password?.message}
                        {...register('password', { required: 'Password is required' })}
                    />
                    <div className={styles.passwordRequirements}>
                        <p>Password requirements:</p>
                        <ul>
                            <li className={passwordValidation.minLength ? styles.valid : ''}>
                                {passwordValidation.minLength ? '✓' : '○'} At least 10 characters
                            </li>
                            <li className={passwordValidation.hasUpperCase ? styles.valid : ''}>
                                {passwordValidation.hasUpperCase ? '✓' : '○'} At least one uppercase letter
                            </li>
                            <li className={passwordValidation.hasLowerCase ? styles.valid : ''}>
                                {passwordValidation.hasLowerCase ? '✓' : '○'} At least one lowercase letter
                            </li>
                            <li className={passwordValidation.hasDigit ? styles.valid : ''}>
                                {passwordValidation.hasDigit ? '✓' : '○'} At least one digit
                            </li>
                            <li className={passwordValidation.hasSpecialChar ? styles.valid : ''}>
                                {passwordValidation.hasSpecialChar ? '✓' : '○'} At least one special character
                            </li>
                        </ul>
                    </div>
                </div>
                {hasTenantAccess && (
                    <Controller
                        name='tenantId'
                        control={control}
                        rules={{ required: 'Tenant is required' }}
                        render={({ field }) => (
                            <Select
                                label='Tenant'
                                value={field.value}
                                onChange={(e) => field.onChange(e.target.value)}
                                error={!!errors.tenantId}
                                errorText={errors.tenantId?.message}
                                options={
                                    selectedTenant
                                        ? [
                                              { value: selectedTenant.id, label: selectedTenant.name },
                                              ...tenants
                                                  .filter((t) => t.id !== selectedTenant.id)
                                                  .map((tenant) => ({ value: tenant.id, label: tenant.name })),
                                          ]
                                        : [
                                              { value: '', label: 'Select a Tenant', disabled: true },
                                              ...tenants.map((tenant) => ({ value: tenant.id, label: tenant.name })),
                                          ]
                                }
                            />
                        )}
                    />
                )}
                <Controller
                    name='roles'
                    control={control}
                    rules={{ validate: (v) => v.length > 0 || 'At least one role is required' }}
                    render={({ field }) => (
                        <Select
                            label='Roles'
                            isMulti
                            value={field.value}
                            onChange={field.onChange}
                            error={!!errors.roles}
                            errorText={errors.roles?.message}
                            placeholder='Select roles...'
                            options={[
                                { value: UserRole.AGENT, label: 'Agent' },
                                { value: UserRole.MANAGER, label: 'Manager' },
                                { value: UserRole.ADMIN, label: 'Admin' },
                                ...(selectedUser && selectedUser.roles.includes(UserRole.SUPER_ADMIN)
                                    ? [{ value: UserRole.SUPER_ADMIN, label: 'Super Admin' }]
                                    : []),
                            ]}
                        />
                    )}
                />

                <div className='flex justify-end gap-md mt-xl'>
                    <Button onClick={handleClose} variant='secondary'>
                        Cancel
                    </Button>
                    <Button type='submit' disabled={isPending}>
                        Add User
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default AddUserModal;
